import {
  OfferDetailsResponse,
  SessionsDate,
  filmCinemasResponse,
  filmExperiencesResponse,
  movieDetailsResponse,
} from '@/logic/services';
import { createStore, stateSetterHandler } from '@alpha.apps/react-common';

import { StateSetter } from '../../types';
import { selectedFilters } from '@/components/movie-details/filters/selected-filters';

interface cinemasParameters {
  cityId: number;
  cinemaIds: number[];
  filterCinemaIds: number[];
  filterExperienceIds: number[];
  showMore: boolean;
  date: string;
  offerId?: string;
}

export type MovieDetailsState = {
  movie: movieDetailsResponse;
  cinemas: filmCinemasResponse;
  offer: OfferDetailsResponse;
  showTimeDate: string;
  firstEnabledDay: SessionsDate;
  filmCinemas: filmCinemasResponse;
  selectedFilters: selectedFilters[];
  selectedCinemasFilter: selectedFilters[];
  selectedExperiencesFilter: selectedFilters[];
  cinemasParameters: cinemasParameters;
  movieCinemasFilter: filmCinemasResponse;
  movieExperiencesFilter: filmExperiencesResponse;
  isFetchingCinemas: boolean;
  isFetchingExperiences: boolean;
  seeResultsDisabled: boolean;
  showMoreBtn: boolean;
  hasMoreCinemas: boolean;
  isFilterMode: boolean;
  isFiltersDrawerOpen: boolean;
  isFetching: boolean;
  isOfferApplied: boolean;
  isThereAtLeastOneAvailableShowTime: boolean;
  movieHasOffers: boolean;
  hasOfferError: boolean;
  hasOfferLoading: boolean;
};

const initState: MovieDetailsState = {
  movie: null,
  cinemas: null,
  offer: null,
  showTimeDate: '',
  firstEnabledDay: null,
  filmCinemas: null,
  selectedFilters: [],
  selectedCinemasFilter: [],
  selectedExperiencesFilter: [],
  cinemasParameters: null,
  movieCinemasFilter: null,
  movieExperiencesFilter: null,
  isFetchingCinemas: true,
  isFetchingExperiences: true,
  seeResultsDisabled: false,
  showMoreBtn: true,
  hasMoreCinemas: false,
  isFilterMode: false,
  isFiltersDrawerOpen: false,
  isFetching: true,
  isOfferApplied: false,
  isThereAtLeastOneAvailableShowTime: false,
  movieHasOffers: false,
  hasOfferError: false,
  hasOfferLoading: false,
};

export type MovieDetailsProps = {
  setMovie: StateSetter<movieDetailsResponse>;
  setCinemas: StateSetter<filmCinemasResponse>;
  setOffer: StateSetter<OfferDetailsResponse>;
  setShowTimeDate: StateSetter<string>;
  setFirstEnabledDay: StateSetter<SessionsDate>;
  setFilmCinemas: StateSetter<filmCinemasResponse>;
  setSelectedFilters: StateSetter<selectedFilters[]>;
  setSelectedCinemasFilter: StateSetter<selectedFilters[]>;
  setSelectedExperiencesFilter: StateSetter<selectedFilters[]>;
  setCinemasParameters: StateSetter<cinemasParameters>;
  setMovieCinemasFilter: StateSetter<filmCinemasResponse>;
  setMovieExperiencesFilter: StateSetter<filmExperiencesResponse>;
  setIsFetchingCinemas: StateSetter<boolean>;
  setIsFetchingExperiences: StateSetter<boolean>;
  setIsSeeResultsDisabled: StateSetter<boolean>;
  setShowMoreBtn: StateSetter<boolean>;
  setHasMoreCinemas: StateSetter<boolean>;
  setIsFilterMode: StateSetter<boolean>;
  setIsFiltersDrawerOpen: StateSetter<boolean>;
  setIsFetching: StateSetter<boolean>;
  setIsOfferApplied: StateSetter<boolean>;
  setIsThereAtLeastOneAvailableShowTime: StateSetter<boolean>;
  setMovieHasOffers: StateSetter<boolean>;
  setHasOfferError: StateSetter<boolean>;
  setHasOfferLoading: StateSetter<boolean>;
};

export const useMovieDetailsState = createStore({
  name: 'movie-details-state',
  devtools: true,
  persist: false,
})(initState, set => {
  const handler = stateSetterHandler(set);

  return {
    setMovie: handler('movie'),
    setCinemas: handler('cinemas'),
    setOffer: handler('offer'),
    setShowTimeDate: handler('showTimeDate'),
    setFirstEnabledDay: handler('firstEnabledDay'),
    setFilmCinemas: handler('filmCinemas'),
    setSelectedFilters: handler('selectedFilters'),
    setSelectedCinemasFilter: handler('selectedCinemasFilter'),
    setSelectedExperiencesFilter: handler('selectedExperiencesFilter'),
    setCinemasParameters: handler('cinemasParameters'),
    setMovieCinemasFilter: handler('movieCinemasFilter'),
    setMovieExperiencesFilter: handler('movieExperiencesFilter'),
    setIsFetchingCinemas: handler('isFetchingCinemas'),
    setIsFetchingExperiences: handler('isFetchingExperiences'),
    setIsSeeResultsDisabled: handler('seeResultsDisabled'),
    setShowMoreBtn: handler('showMoreBtn'),
    setHasMoreCinemas: handler('hasMoreCinemas'),
    setIsFilterMode: handler('isFilterMode'),
    setIsFiltersDrawerOpen: handler('isFiltersDrawerOpen'),
    setIsFetching: handler('isFetching'),
    setIsOfferApplied: handler('isOfferApplied'),
    setIsThereAtLeastOneAvailableShowTime: handler('isThereAtLeastOneAvailableShowTime'),
    setMovieHasOffers: handler('movieHasOffers'),
    setHasOfferError: handler('hasOfferError'),
    setHasOfferLoading: handler('hasOfferLoading'),
  } as MovieDetailsProps;
});
